/**
 * useEdit Hook
 *
 * Hook for handling post editing with both AI rewrite and manual edit capabilities.
 * Follows the same pattern as useApproval and useDraftGeneration.
 *
 * AI Rewrite Flow:
 * 1. Update status to "Editing"
 * 2. Trigger webhook for AI processing
 * 3. Poll until status changes to "Pending Approval"
 *
 * Manual Edit Flow:
 * 1. Update Supabase directly with edited content
 * 2. Status remains "Pending Approval"
 * 3. No webhook trigger needed
 */

import { useState, useCallback, useRef } from "react";
import { triggerEditWithAI } from "../api/webhook";
import { updateBlogPost, getBlogPostById } from "../api/supabase";

/**
 * Hook for managing post edit actions
 * @returns {{
 *   editWithAI: Function,
 *   manualEdit: Function,
 *   loading: boolean,
 *   loadingStage: string,
 *   success: boolean,
 *   error: string|null,
 *   successData: Object|null,
 *   resetState: Function
 * }}
 */
export const useEdit = () => {
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("idle");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  const pollingIntervalRef = useRef(null);

  const resetState = useCallback(() => {
    setLoading(false);
    setLoadingStage("idle");
    setSuccess(false);
    setError(null);
    setSuccessData(null);
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
    }
  }, []);

  /**
   * Edit post with AI rewrite
   * @param {string} postId - Blog Post record ID
   * @param {Object} editData - Edit instructions
   * @param {string} editData.editInstructions - Main edit instructions
   * @param {string} editData.customPrompt - Additional custom prompt
   */
  const editWithAI = useCallback(async (postId, editData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setLoadingStage("starting");

      console.log("Starting AI rewrite process for post:", postId);

      // Step 1: Update status to "Editing"
      setLoadingStage("updating-status");

      // Only update Status - don't try to update "Edit Notes" field if it doesn't exist
      // The edit instructions are sent to n8n via webhook payload instead
      await updateBlogPost(postId, {
        Status: "Editing",
      });
      console.log("Post status updated to 'Editing'");

      // Step 2: Trigger webhook
      setLoadingStage("triggering-workflow");
      const webhookResult = await triggerEditWithAI(postId, editData);

      if (!webhookResult.success) {
        throw new Error(`Webhook failed: ${webhookResult.error}`);
      }

      console.log("AI rewrite workflow triggered successfully");

      // Step 3: Poll for completion
      setLoadingStage("processing");

      const pollForCompletion = () => {
        return new Promise((resolve, reject) => {
          const startTime = Date.now();
          const maxDuration = 5 * 60 * 1000; // 5 minutes

          const checkStatus = async () => {
            try {
              const updatedPost = await getBlogPostById(postId);

              // Check if AI has completed rewrite (status back to Pending Approval)
              if (updatedPost?.status === "Pending Approval") {
                console.log("AI rewrite completed successfully");
                resolve(updatedPost);
                return;
              }

              // Check for error states
              if (updatedPost?.status === "Rejected") {
                reject(new Error("Post was rejected during AI rewrite"));
                return;
              }

              // Check timeout
              if (Date.now() - startTime > maxDuration) {
                reject(new Error("AI rewrite process timed out after 5 minutes"));
                return;
              }

              // Continue polling
              pollingIntervalRef.current = setTimeout(checkStatus, 5000); // Check every 5 seconds
            } catch (err) {
              console.error("Error checking post status:", err);
              reject(err);
            }
          };

          checkStatus();
        });
      };

      const finalPost = await pollForCompletion();

      setLoadingStage("complete");
      setSuccess(true);
      setSuccessData({ post: finalPost });

      return { success: true, data: finalPost };
    } catch (err) {
      console.error("Error in AI rewrite process:", err);
      setLoadingStage("error");
      setError(err.message);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    }
  }, []);

  /**
   * Manual edit post (direct Supabase update)
   * @param {string} postId - Blog Post record ID
   * @param {Object} editedContent - Edited content fields
   * @param {string} editedContent.content - Post content (HTML from RSS)
   */
  const manualEdit = useCallback(async (postId, editedContent) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setLoadingStage("starting");

      console.log("Starting manual edit for post:", postId);

      setLoadingStage("updating-content");

      // Prepare update data for all editable fields
      const updateData = {};
      if (editedContent.content !== undefined) updateData.content = editedContent.content;
      if (editedContent.linkedinCopy !== undefined)
        updateData.linkedinCopy = editedContent.linkedinCopy;
      if (editedContent.xCopy !== undefined) updateData.xCopy = editedContent.xCopy;
      if (editedContent.threadsCopy !== undefined)
        updateData.threadsCopy = editedContent.threadsCopy;

      await updateBlogPost(postId, updateData);

      console.log("Manual edit saved successfully");

      // Fetch updated post
      const updatedPost = await getBlogPostById(postId);

      setLoadingStage("complete");
      setSuccess(true);
      setSuccessData({ post: updatedPost });

      return { success: true, data: updatedPost };
    } catch (err) {
      console.error("Error in manual edit process:", err);
      setLoadingStage("error");
      setError(err.message);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    editWithAI,
    manualEdit,
    loading,
    loadingStage,
    success,
    error,
    successData,
    resetState,
  };
};

export default useEdit;
